// マイページ：ランキングに出る名前・クラウドのセーブ・自分の記録・設定。
// 名前は「名前を変える」（決めていない人は「名前を決める」）で、ログインのあとと同じ窓（ui/name-dialog.ts）を開いて変える。
import { boardDocId } from "@appful/sdk/protocol";
import { collection, doc, getDoc, getDocs, type Timestamp } from "firebase/firestore";
import { track } from "../analytics";
import { deleteAccount, onSession, session, signOut } from "../auth";
import { config } from "../config";
import { entitlement, onEntitlement, openPortal, startCheckout } from "../entitlement";
import { loadCatalog, type CatalogGame } from "../catalog";
import { db } from "../firebase";
import { getLocale, pick, t } from "../i18n";
import { clearDeviceData } from "../local";
import { resetSaves } from "../saves";
import { openSignIn, toast } from "./dialogs";
import { h } from "./dom";
import { icon } from "./icons";
import { gamePath } from "./parts";
import type { View } from "./view";

function when(ts: Timestamp | undefined): string {
  if (!ts) return "";
  return ts.toDate().toLocaleString(getLocale(), { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

/** 押すと注意と実行のボタンが出る、2段階の確認 */
function confirmBox(warn: string, yesLabel: string, run: () => Promise<void>, msg: HTMLElement): HTMLElement {
  const yes = h("button", { class: "btn-danger", type: "button" }, yesLabel);
  yes.addEventListener("click", async () => {
    yes.disabled = true;
    try {
      await run();
    } catch (e) {
      console.error(e);
      msg.textContent = t("error.generic");
      yes.disabled = false;
    }
  });
  return h("div", { class: "danger-confirm", hidden: true }, h("p", { class: "ph" }, warn), yes);
}

/** 広告を消すサブスクのカード */
function adFreeCard(signedIn: boolean, msg: HTMLElement): HTMLElement {
  const e = entitlement();
  const fmtDate = (d: Date) => d.toLocaleDateString(getLocale(), { year: "numeric", month: "numeric", day: "numeric" });
  const card = h("div", { class: "card adfree", "data-testid": "adfree-card" }, h("h2", { class: "h3" }, t("adfree.title")));
  const legal = config.legalUrl ? h("p", { class: "small" }, h("a", { href: config.legalUrl }, t("adfree.legal"))) : null;
  if (e.adFree) {
    const manage = h("button", { class: "btn-sub", type: "button", "data-testid": "adfree-manage" }, t("adfree.manage"));
    manage.addEventListener("click", async () => {
      manage.disabled = true;
      try {
        await openPortal();
      } catch (err) {
        console.error(err);
        msg.textContent = t("adfree.failed");
        manage.disabled = false;
      }
    });
    card.append(
      ...([
        h("p", { class: "ph", "data-testid": "adfree-active" }, h("b", {}, t("adfree.active"))),
        e.periodEnd ? h("p", { class: "small" }, t(e.cancelAtPeriodEnd ? "adfree.ends" : "adfree.renews", { date: fmtDate(e.periodEnd) })) : null,
        h("div", { class: "actions" }, manage),
        legal,
      ] as (HTMLElement | null)[]).filter((x): x is HTMLElement => x !== null),
    );
    return card;
  }
  card.append(h("p", { class: "price" }, t("adfree.price", { yen: config.billingPriceYen.toLocaleString(getLocale()) })), h("p", { class: "ph" }, t("adfree.lead")));
  if (!signedIn) {
    card.append(h("p", { class: "small ph" }, t("adfree.signin")), h("div", { class: "actions" }, h("button", { class: "btn-login", type: "button", onclick: () => void openSignIn() }, t("auth.signIn"))));
    return card;
  }
  const check = h("input", { type: "checkbox", id: "adfree-consent", "data-testid": "adfree-consent" });
  const go = h("button", { class: "btn-play sm", type: "button", disabled: true, "data-testid": "adfree-subscribe" }, t("adfree.subscribe"));
  check.addEventListener("change", () => (go.disabled = !check.checked));
  go.addEventListener("click", async () => {
    go.disabled = true;
    try {
      await startCheckout(check.checked);
    } catch (err) {
      console.error(err);
      msg.textContent = t("adfree.failed");
      go.disabled = false;
    }
  });
  card.append(
    h("label", { class: "consent", for: "adfree-consent" }, check, h("span", { class: "ph" }, t("adfree.consent"))),
    h("div", { class: "actions" }, go),
    h("p", { class: "small ph" }, t("adfree.note")),
    ...(legal ? [legal] : []),
  );
  return card;
}

export function meView(switchLocale: () => void): View {
  const el = h("div", { class: "wrap narrow", id: "me" });
  let seq = 0;

  const render = async () => {
    const my = ++seq;
    const s = session();
    document.title = `${t("me.title")}｜appful games`;
    if (!s.ready) return;
    const games = await loadCatalog().catch(() => [] as CatalogGame[]);
    if (my !== seq) return;
    const byId = new Map(games.map((g) => [g.id, g]));
    const msg = h("p", { class: "form-msg", "aria-live": "polite" });

    // ランキングに出る名前
    const rename = h(
      "button",
      { class: "btn-text", type: "button", "data-testid": "name-change", onclick: () => void import("./name-dialog").then((m) => m.openNameDialog({ from: "me" })) },
      t(s.nameChosen ? "me.nameChange" : "me.nameChoose"),
    );
    const head = s.user
      ? h(
          "div",
          { class: "card me" },
          h("span", { class: "me-ava" }, icon("user")),
          h(
            "span",
            { class: "me-b" },
            h("span", { class: "me-n", "data-testid": "nickname" }, s.displayName ?? "", s.tag && h("span", { class: "tag-no" }, `#${s.tag}`)),
            h("span", { class: "small ph" }, t(s.nameChosen ? "me.nameNote" : "me.nameNoteUnchosen")),
          ),
          rename,
        )
      : h(
          "div",
          { class: "card me" },
          h("span", { class: "me-ava" }, icon("user")),
          h("span", { class: "me-b" }, h("span", { class: "me-n" }, t("me.guest")), h("span", { class: "small ph" }, t("me.guestNote"))),
          h("button", { class: "btn-login", type: "button", onclick: () => void openSignIn() }, t("auth.signIn")),
        );

    // セーブと記録（ログイン中だけ。Firestore から自分の物を読む）
    const saves = h("ul", { class: "sv-list" }, h("li", { class: "small" }, s.user ? t("play.loading") : t("me.savesGuest")));
    const records = h("tbody", {});
    const recordsCard = h(
      "div",
      { class: "card", hidden: !s.user },
      h("h2", { class: "h3" }, icon("trophy"), t("me.records")),
      h("table", { class: "tbl" }, h("thead", {}, h("tr", {}, h("th", {}, t("me.col.game")), h("th", {}, t("me.col.best")))), records),
    );
    if (s.user) {
      const uid = s.user.uid;
      getDocs(collection(db, "users", uid, "saves"))
        .then((snap) => {
          if (my !== seq) return;
          const rows = snap.docs
            .map((d) => ({ game: byId.get(d.id), at: d.get("updatedAt") as Timestamp | undefined }))
            .filter((r): r is { game: CatalogGame; at: Timestamp | undefined } => !!r.game)
            .sort((a, b) => (b.at?.toMillis() ?? 0) - (a.at?.toMillis() ?? 0));
          saves.replaceChildren(
            ...(rows.length
              ? rows.map((r) =>
                  h(
                    "li",
                    { class: "sv" },
                    h("img", { src: r.game.thumbnail, alt: "", loading: "lazy" }),
                    h("span", { class: "sv-b" }, h("span", { class: "tt ph" }, pick(r.game.title)), h("span", { class: "saved" }, icon("check"), t("saved.cloud"), " ", h("span", { class: "nw" }, when(r.at)))),
                    h("a", { class: "btn-mini", href: gamePath(r.game) }, t("home.continue")),
                  ),
                )
              : [h("li", { class: "small" }, t("me.savesEmpty"))]),
          );
        })
        .catch((e) => {
          console.error(e);
          saves.replaceChildren(h("li", { class: "small" }, t("error.generic")));
        });
      // 自己ベスト：「これまで」のランキングがあるゲームの、自分の行を読む
      const boards = games.flatMap((g) => g.leaderboards.filter((b) => b.periods.includes("all")).map((b) => ({ g, b })));
      Promise.all(boards.map(({ g, b }) => getDoc(doc(db, "leaderboards", boardDocId(g.id, b.id, "all"), "entries", uid)).then((d) => ({ g, score: d.exists() ? Number(d.get("score")) : null }))))
        .then((rows) => {
          if (my !== seq) return;
          const have = rows.filter((r) => r.score !== null);
          records.replaceChildren(
            ...(have.length
              ? have.map((r) => h("tr", {}, h("td", { class: "ph" }, pick(r.g.title)), h("td", { class: "num nw" }, (r.score as number).toLocaleString(getLocale()))))
              : [h("tr", {}, h("td", { colspan: 2, class: "small" }, t("me.recordsEmpty")))]),
          );
        })
        .catch((e) => console.error(e));
    }

    // 広告を消すサブスク（ログインした人だけが申し込める。本番は表示のページができるまで出さない）
    const adfree = config.billingEnabled && (config.useEmulators || config.legalUrl) ? adFreeCard(!!s.user, msg) : null;

    // 設定
    const clearConfirm = confirmBox(
      t("me.clearWarn"),
      t("me.clearYes"),
      async () => {
        clearDeviceData();
        resetSaves();
        toast(t("me.cleared"));
        void render();
      },
      msg,
    );
    const deleteConfirm = confirmBox(
      t("me.deleteWarn"),
      t("me.deleteYes"),
      async () => {
        await deleteAccount();
        track("account_delete", {});
        toast(t("me.deleted"));
      },
      msg,
    );
    const seg = (active: boolean, label: string) => h("button", { type: "button", class: active ? "on" : "", "aria-pressed": String(active), onclick: active ? undefined : switchLocale }, label);
    el.replaceChildren(
      head,
      h("div", { class: "card" }, h("h2", { class: "h3" }, icon("resume"), t("me.saves")), saves),
      recordsCard,
      ...(adfree ? [adfree] : []),
      h(
        "div",
        { class: "card" },
        h("h2", { class: "h3" }, t("me.settings")),
        h(
          "ul",
          { class: "set" },
          h("li", {}, h("span", {}, t("me.language")), h("span", { class: "seg" }, seg(getLocale() === "ja", "日本語"), seg(getLocale() === "en", "English"))),
          h("li", {}, h("span", {}, t("me.deviceData")), h("button", { class: "btn-text", type: "button", onclick: () => (clearConfirm.hidden = false) }, t("me.clear")), clearConfirm),
          s.user &&
            h(
              "li",
              {},
              h("span", {}, t("me.account")),
              h("button", { class: "btn-text", type: "button", "data-testid": "signout", onclick: () => void signOut() }, t("me.signOut")),
              h("button", { class: "btn-text warn", type: "button", "data-testid": "delete-account", onclick: () => (deleteConfirm.hidden = false) }, t("me.delete")),
              deleteConfirm,
            ),
        ),
        msg,
      ),
    );
  };

  // 支払いの画面から戻ったとき（?billing=success / cancel）に一言出す
  const back = new URLSearchParams(location.search).get("billing");
  if (back === "success" || back === "cancel") {
    toast(t(back === "success" ? "adfree.thanks" : "adfree.cancelled"));
    history.replaceState(null, "", location.pathname);
  }
  const off = onSession(() => void render());
  const offEnt = onEntitlement(() => void render());
  void render();
  return {
    el,
    destroy: () => {
      off();
      offEnt();
    },
    onLocale: () => void render(),
  };
}
